declare global {
  interface Window {
    fbq: (...args: unknown[]) => void
    _fbq: (...args: unknown[]) => void
  }
}

const HOTMART_DOMAIN = 'pay.hotmart.com'

// ─── UTM & fbclid reader ───────────────────────────────────────
function getTrackingParams(): {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_content: string
  utm_term: string
  fbclid: string
} {
  const params = new URLSearchParams(window.location.search)
  return {
    utm_source: params.get('utm_source') || 'none',
    utm_medium: params.get('utm_medium') || 'none',
    utm_campaign: params.get('utm_campaign') || 'none',
    utm_content: params.get('utm_content') || 'none',
    utm_term: params.get('utm_term') || 'none',
    fbclid: params.get('fbclid') || '',
  }
}

// ─── Build sck/src string ──────────────────────────────────────
function buildSckValue(tp: ReturnType<typeof getTrackingParams>): string {
  return [
    tp.utm_source,
    tp.utm_medium,
    tp.utm_campaign,
    tp.utm_content,
    tp.utm_term,
  ].join('|')
}

// ─── Decorate a single Hotmart link ────────────────────────────
function decorateLink(
  anchor: HTMLAnchorElement,
  tp: ReturnType<typeof getTrackingParams>,
): void {
  // Skip if already decorated
  if (anchor.dataset.htDecorated === '1') return

  try {
    const url = new URL(anchor.href)
    if (!url.hostname.includes(HOTMART_DOMAIN)) return

    const sck = buildSckValue(tp)
    if (sck) {
      url.searchParams.set('sck', sck)
      url.searchParams.set('src', sck)
    }
    if (tp.fbclid) {
      url.searchParams.set('fbclid', tp.fbclid)
    }

    anchor.href = url.toString()
    anchor.dataset.htDecorated = '1'
  } catch {
    // Invalid URL, skip
  }
}

// ─── Scan & decorate all Hotmart links in the DOM ──────────────
function decorateAllLinks(tp: ReturnType<typeof getTrackingParams>): void {
  const anchors = document.querySelectorAll<HTMLAnchorElement>(
    'a[href*="pay.hotmart.com"]',
  )
  anchors.forEach((a) => decorateLink(a, tp))
}

// ─── Attach InitiateCheckout click listeners ───────────────────
function attachClickTracking(): void {
  const anchors = document.querySelectorAll<HTMLAnchorElement>(
    'a[href*="pay.hotmart.com"]',
  )
  anchors.forEach((a) => {
    if (a.dataset.htClickTracked === '1') return
    a.addEventListener('click', () => {
      trackInitiateCheckout()
    })
    a.dataset.htClickTracked = '1'
  })
}

// ─── Meta Pixel initialization ─────────────────────────────────
function initPixel(pixelId: string): void {
  if (window.fbq) return

  const n = (window.fbq = function (...args: unknown[]) {
    if ((n as any).callMethod) {
      ;(n as any).callMethod.apply(n, args)
    } else {
      ;(n as any).queue.push(args)
    }
  } as any)

  if (!window._fbq) window._fbq = n
  ;(n as any).push = n
  ;(n as any).loaded = true
  ;(n as any).version = '2.0'
  ;(n as any).queue = []

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const firstScript = document.getElementsByTagName('script')[0]
  firstScript?.parentNode?.insertBefore(script, firstScript)

  window.fbq('init', pixelId)
  window.fbq('track', 'PageView')
}

// ─── Public: fire InitiateCheckout ─────────────────────────────
export function trackInitiateCheckout(): void {
  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout')
  }
}

// ─── Public: fire AddToCart (kept for backward compat) ─────────
export function trackAddToCart(): void {
  if (window.fbq) {
    window.fbq('track', 'AddToCart')
  }
}

// ─── Main entry point ──────────────────────────────────────────
export function initHotmartTracking(pixelId: string): void {
  // 1. Initialize Meta Pixel + PageView
  initPixel(pixelId)

  // 2. Read UTMs & fbclid from current URL
  const tp = getTrackingParams()

  // 3. Initial decoration pass
  decorateAllLinks(tp)
  attachClickTracking()

  // 4. MutationObserver for lazy-loaded sections
  const observer = new MutationObserver(() => {
    decorateAllLinks(tp)
    attachClickTracking()
  })
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // 5. Fallback setInterval for any edge cases (stops after 30s)
  const intervalId = setInterval(() => {
    decorateAllLinks(tp)
    attachClickTracking()
  }, 2000)

  setTimeout(() => clearInterval(intervalId), 30000)
}
